
import React, { useState } from 'react';
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { 
  ArrowRight, 
  Check,
  Clock, 
  DollarSign, 
  FileText, 
  Filter, 
  MoreVertical, 
  Star, 
  TrendingUp, 
  Users 
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Proposal } from "@/types/proposals";

const dummyProposals: Proposal[] = [
  {
    id: 1,
    projectTitle: "E-commerce Website Development",
    clientName: "John Smith",
    status: "pending",
    submittedDate: "2024-02-15",
    bidAmount: 2500,
    description: "Full-stack development of an e-commerce platform",
    clientRating: 4.8,
    competingProposals: 12,
    averageBid: 2800,
    clientLocation: "United States",
    projectDuration: "3 months",
    clientVerified: true,
    skills: ["React", "Node.js", "MongoDB"],
    milestones: [
      { title: "Frontend Development", amount: 1000 },
      { title: "Backend Integration", amount: 1500 }
    ],
    deadline: "2024-04-15",
    proposalDescription: "Detailed proposal with project timeline",
    duration: "3 months",
    responseTime: "2 days",
    deliverables: ["UI/UX Design", "Frontend", "Backend", "Testing", "Deployment"],
    clientCompany: "E-Commerce Solutions Inc.",
    clientProjects: 15,
    clientResponseRate: 92,
    submissionDate: "2024-02-15",
    responseDate: "2024-02-17",
    clientResponseTime: "48 hours",
    projectCategory: "Web Development",
    winProbability: 75
  },
  // Add more dummy proposals as needed
];

const ProposalCard = ({ proposal, onViewProposal }: { proposal: Proposal, onViewProposal: (proposal: Proposal) => void }) => {
  return (
    <Card className="mb-4">
      <CardContent className="p-6">
        <div className="flex justify-between items-start mb-4">
          <div>
            <h3 className="text-lg font-semibold mb-1">{proposal.projectTitle}</h3>
            <div className="flex items-center gap-2 text-sm text-gray-500">
              <Users className="w-4 h-4" />
              <span>{proposal.clientName}</span>
              {proposal.clientVerified && (
                <Badge variant="secondary" className="bg-green-100 text-green-800">
                  Verified
                </Badge>
              )}
            </div>
          </div>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon">
                <MoreVertical className="w-4 h-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => onViewProposal(proposal)}>
                View Details
              </DropdownMenuItem>
              <DropdownMenuItem>Edit Proposal</DropdownMenuItem>
              <DropdownMenuItem className="text-red-600">
                Withdraw
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
          <div>
            <div className="text-sm text-gray-500">Bid Amount</div>
            <div className="font-semibold flex items-center">
              <DollarSign className="w-4 h-4" />
              {proposal.bidAmount}
            </div>
          </div>
          <div>
            <div className="text-sm text-gray-500">Submitted</div>
            <div className="font-semibold flex items-center">
              <Clock className="w-4 h-4 mr-1" />
              {proposal.submittedDate}
            </div>
          </div>
          <div>
            <div className="text-sm text-gray-500">Status</div>
            <Badge
              variant={
                proposal.status === "accepted"
                  ? "success"
                  : proposal.status === "rejected"
                  ? "destructive"
                  : "default"
              }
            >
              {proposal.status.charAt(0).toUpperCase() + proposal.status.slice(1)}
            </Badge>
          </div>
          <div>
            <div className="text-sm text-gray-500">Client Rating</div>
            <div className="flex items-center">
              <Star className="w-4 h-4 text-yellow-400 mr-1" />
              {proposal.clientRating}
            </div>
          </div>
        </div>

        <div className="flex flex-wrap gap-2 mb-4">
          {proposal.skills.map((skill, index) => (
            <Badge key={index} variant="outline">
              {skill}
            </Badge>
          ))}
        </div>

        <div className="flex justify-between items-center">
          <div className="text-sm text-gray-500">
            {proposal.competingProposals} competing proposals
          </div>
          <Button
            variant="ghost"
            size="sm"
            className="gap-2"
            onClick={() => onViewProposal(proposal)}
          >
            View Details <ArrowRight className="w-4 h-4" />
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

const ProposalStats = () => {
  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
      <Card>
        <CardHeader>
          <CardTitle className="text-sm font-medium">Win Rate</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center">
            <Check className="w-5 h-5 text-green-500 mr-2" />
            <div className="text-2xl font-bold">68%</div>
          </div>
          <Progress value={68} className="mt-2" />
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-sm font-medium">Success Rate</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center">
            <Check className="w-5 h-5 text-green-500 mr-2" />
            <div className="text-2xl font-bold">92%</div>
          </div>
          <Progress value={92} className="mt-2" />
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-sm font-medium">Response Rate</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center">
            <Check className="w-5 h-5 text-green-500 mr-2" />
            <div className="text-2xl font-bold">85%</div>
          </div>
          <Progress value={85} className="mt-2" />
        </CardContent>
      </Card>
    </div>
  );
};

const Proposals = () => {
  const [activeTab, setActiveTab] = useState("all");
  const [selectedProposal, setSelectedProposal] = useState<Proposal | null>(null);

  const handleViewProposal = (proposal: Proposal) => {
    setSelectedProposal(proposal);
  };

  return (
    <div className="container mx-auto p-6">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-3xl font-bold mb-1">Proposals</h1>
          <p className="text-gray-500">Track and manage your project proposals</p>
        </div>
        <Button className="gap-2">
          <FileText className="w-4 h-4" /> New Proposal
        </Button>
      </div>

      <ProposalStats />

      <div className="flex justify-between items-center mb-6">
        <Tabs defaultValue={activeTab} className="w-full" onValueChange={setActiveTab}>
          <div className="flex justify-between items-center">
            <TabsList>
              <TabsTrigger value="all">All Proposals</TabsTrigger>
              <TabsTrigger value="active">Active</TabsTrigger>
              <TabsTrigger value="pending">Pending</TabsTrigger>
              <TabsTrigger value="completed">Completed</TabsTrigger>
            </TabsList>
            <Button variant="outline" size="sm" className="gap-2">
              <Filter className="w-4 h-4" /> Filter
            </Button>
          </div>

          <TabsContent value="all" className="mt-6">
            {dummyProposals.map((proposal) => (
              <ProposalCard
                key={proposal.id}
                proposal={proposal}
                onViewProposal={handleViewProposal}
              />
            ))}
          </TabsContent>

          <TabsContent value="active">
            {dummyProposals
              .filter((p) => p.status === "accepted")
              .map((proposal) => (
                <ProposalCard
                  key={proposal.id}
                  proposal={proposal}
                  onViewProposal={handleViewProposal}
                />
              ))}
          </TabsContent>

          <TabsContent value="pending">
            {dummyProposals
              .filter((p) => p.status === "pending")
              .map((proposal) => (
                <ProposalCard
                  key={proposal.id}
                  proposal={proposal}
                  onViewProposal={handleViewProposal}
                />
              ))}
          </TabsContent>

          <TabsContent value="completed">
            {dummyProposals
              .filter((p) => p.status === "completed" || p.status === "accepted")
              .map((proposal) => (
                <ProposalCard
                  key={proposal.id}
                  proposal={proposal}
                  onViewProposal={handleViewProposal}
                />
              ))}
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default Proposals;
